import type {
  ChatroomDataObject,
  ChatroomTag,
  MessageDataObject,
  MessagePlaceholderDataObject,
  MessageViewDataObject,
  ParticipantDataObject,
  UserDataObject,
} from '@pba/react-chat-core';

export type ChatMessage = Message | MessagePlaceholder;

export type ChatRoom = {
  author?: string;
  createdAt: Date;
  id: string;
  message?: ChatMessage;
  name?: string;
  participants: Record<string, Participant>;
  stats?: ChatroomUserStats;
  status?: string;
  tags: ChatroomTag[];
  updatedAt: Date;
  users: string[];
};

export type ChatroomUserStats = {
  unread: number;
  unseen?: number;
};

export type Message = {
  author?: string;
  chatroom: string;
  createdAt: Date;
  id: string;
  payload: MessagePayload[];
  text?: string;
  type: MessageType;
  updatedAt?: Date;
};

export type MessagePlaceholder = {
  chatroom: string;
  createdAt: Date;
  id: string;
  payload: MessagePayload[];
  status?: MessageDeliveryStatus;
  text?: string;
  type: 'message-placeholder';
  updatedAt: Date;
};

type KnownMessageType = 'message-placeholder' | 'message';

type MessageType = ({} & string) | KnownMessageType;

export const createChatroom = (data: ChatroomDataObject): ChatRoom => ({
  author: data.author,
  createdAt: new Date(data.createdAt ?? 0),
  id: data._id,
  message: data.message ? createMessage(data.message) : undefined,
  name: data.name,
  participants: Object.fromEntries(
    Object.entries(data.participants).map(([userId, participant]) => [userId, createParticipant(participant)]),
  ),
  stats: data.stats,
  status: data.status,
  tags: data.tags ?? [],
  updatedAt: new Date(data.updatedAt ?? 0),
  users: data.users,
});

const isMessagePlaceholderDataObject = (data: MessageViewDataObject): data is MessagePlaceholderDataObject =>
  data.type === 'message-placeholder';

export const createMessage = (data: MessageViewDataObject): ChatMessage => {
  if (isMessagePlaceholderDataObject(data)) {
    return {
      chatroom: data.chatroom,
      createdAt: new Date(data.createdAt),
      id: data._id,
      payload: data.payload,
      status: data.status,
      text: data.text,
      type: 'message-placeholder',
      updatedAt: new Date(data.updatedAt),
    };
  }

  return {
    author: data.author,
    chatroom: data.chatroom,
    // TODO: Fix this when the API is updated to include createdAt
    createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
    id: data._id,
    payload: data.payload ?? [],
    text: data.text,
    type: data.type,
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : undefined,
  };
};

export type ChatUser = {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
};

export type MessageDeliveryStatus = 'failed' | 'saving' | 'sending' | 'sent';

export type MessageFilePayload = {
  meta: {
    mimeType: string;
    name: string;
  };
  ref: string;
};

export type MessageImagePayload = {
  meta: {
    mimeType: string;
    name?: string;
  };
  ref: string;
};

export type MessageVideoPayload = {
  meta: {
    mimeType: string;
    name?: string;
  };
  ref: string;
};

type MessagePayload = NonNullable<MessageDataObject['payload']>[number];

export const isImageContentType = (contentType: string) => contentType.startsWith('image/');

export const isMessageFilePayload = (payload: MessagePayload): payload is MessageFilePayload =>
  typeof payload.ref === 'string' &&
  typeof payload.meta?.mimeType === 'string' &&
  typeof payload.meta.name === 'string' &&
  !isImageContentType(payload.meta.mimeType) &&
  !payload.meta.mimeType.startsWith('video/');

export const isMessageImagePayload = (payload: MessagePayload): payload is MessageImagePayload =>
  typeof payload.ref === 'string' &&
  typeof payload.meta?.mimeType === 'string' &&
  isImageContentType(payload.meta.mimeType);

export const isMessageVideoPayload = (payload: MessagePayload): payload is MessageVideoPayload =>
  typeof payload.ref === 'string' &&
  typeof payload.meta?.mimeType === 'string' &&
  payload.meta.mimeType.startsWith('video/');

export const hasMessageDisplayContent = (message: Pick<ChatMessage, 'payload' | 'text'>): boolean =>
  Boolean(message.text?.trim()) ||
  message.payload.some((payload) => typeof payload.ref === 'string' && typeof payload.meta?.mimeType === 'string');

export const createChatUser = (data: UserDataObject): ChatUser => ({
  email: data.email,
  firstName: data.firstName ?? '',
  id: data._id,
  lastName: data.lastName ?? '',
});

export const createParticipant = (data: ParticipantDataObject): Participant => ({
  email: data.email,
  firstName: data.firstName,
  lastName: data.lastName,
});

export type Participant = {
  email: string;
  firstName: string;
  lastName: string;
};

export const isGroupChatroom = ({ chatroom }: { chatroom: Pick<ChatRoom, 'tags'> }): boolean =>
  chatroom.tags.includes('group');

export const isMessagePlaceholder = (message: ChatMessage): message is MessagePlaceholder =>
  message.type === 'message-placeholder';
